/* eslint-disable complexity */
import React, { ForwardRefExoticComponent, useContext } from 'react';
import {
	IDates,
	IOffer,
	IOrderInfoDetails,
	ITypeVignetteOrderingProcessFields,
	IVignetteOffer,
	OfferContext,
	VignetteContext,
	calculateNewDates,
	convertApiDate,
	getCorrespondingTransitionVignette,
	getPriceAmountByCatalogId,
	oneDayVignettes,
} from '@adac-mp/shared';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addDays, getDate } from 'date-fns';
import de from 'date-fns/locale/de'; // the locale you want
import en from 'date-fns/locale/en-US';
import { HintMessage, Typography, getHintContent, renderErrorMessage } from '../../index';
import { useViewport } from '../../hooks/useViewport';
import { ICustomInputProps } from '../../utils/renderCustomInput';
import { useFlowId } from '../../hooks/useFlowId';

interface IStartDatePickerProps {
	inline?: boolean;
	customInput?: ForwardRefExoticComponent<ICustomInputProps>;
	dates?: IDates;
	hasError?: boolean;
	offer?: IVignetteOffer | IOrderInfoDetails;
	onStartDateChange: (vignette?: IVignetteOffer | IOffer | IOrderInfoDetails, dates?: IDates) => void;
	'data-cy'?: string;
}

const renderDayContents = (_, date) => {
	const tooltipText = `Tooltip for date: ${date}`;
	return (
		<span className="inline-flex justify-center items-center" title={tooltipText}>
			{getDate(date)}
		</span>
	);
};

export const StartDatePicker = ({
	customInput,
	inline,
	dates,
	hasError,
	offer,
	onStartDateChange,
	'data-cy': dataCy = 'calendar-input',
}: IStartDatePickerProps) => {
	const usedLocales = {
		en,
		de,
	};
	const { lang } = useContext(OfferContext);
	const { selectedProducts, isTransitionPeriod, contentfulLabels, productsTypeMap } = useContext(VignetteContext);
	const currSelectedProduct = selectedProducts?.find((p) => p.moduleTypeId === 2);
	const currAutOffer = offer ?? selectedProducts?.find((p) => p.moduleTypeId === 2 && p.supplierId === 3);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const { vignetteOffers } = useContext(OfferContext);
	const { viewport } = useViewport();
	const currentOffer = offer ?? currSelectedProduct;
	const correspondingTransitionVignette = getCorrespondingTransitionVignette(
		isTransitionPeriod,
		vignetteOffers,
		currentOffer as IVignetteOffer,
	);
	const nextYear = new Date().getFullYear() + 1;
	const flowId = useFlowId();
	const isSlovenianFlow = flowId === 7;
	const hasAutVignette = productsTypeMap.get('autVignette');
	const hasSloVignette = productsTypeMap.get('sloVignette');
	const onlySloOneCart = !hasAutVignette && hasSloVignette;
	const excludeDateIntervals = isSlovenianFlow || onlySloOneCart;

	registerLocale(lang ?? 'de', usedLocales[lang ?? 'de']);

	const getNextYearVignette = (): IVignetteOffer | IOffer | IOrderInfoDetails | undefined => {
		if (!isTransitionPeriod) return currentOffer;
		return (
			vignetteOffers.find(
				(vOffer) =>
					vOffer.serviceCatalogCode === currentOffer?.serviceCatalogCode &&
					vOffer.servcatSeason === String(new Date().getFullYear() + 1),
			) || currentOffer
		);
	};

	const currentVignetteIsNextYearVignette = (vignette?: IVignetteOffer | IOffer | IOrderInfoDetails) =>
		vignette?.servcatSeason === getNextYearVignette()?.servcatSeason;

	const vignetteForEndDate = getNextYearVignette();
	const getVignetteStartDate = () => {
		if (isTransitionPeriod && currentOffer && correspondingTransitionVignette) return new Date();
		if (currentOffer) {
			return convertApiDate(currentOffer?.usageFrom).getTime() < new Date().getTime()
				? new Date()
				: convertApiDate(currentOffer?.usageFrom);
		}
		return new Date();
	};

	const vignetteStartDate = getVignetteStartDate();
	const onChange = (newDates: Date[]) => {
		if (currentOffer) {
			const [start] = newDates;
			const updatedDates = calculateNewDates(start, currentOffer);
			if (currentOffer && correspondingTransitionVignette && updatedDates) {
				const isNextYearVignette = currentVignetteIsNextYearVignette(currentOffer);
				const currentOfferSellToDateTime = convertApiDate(currentOffer.usageTo).getTime();
				const correspondingOfferSellToDateTime = convertApiDate(
					correspondingTransitionVignette.usageTo,
				).getTime();
				const newStartDateTime = newDates[0].getTime();
				if (
					(currentOfferSellToDateTime < newStartDateTime && !isNextYearVignette) ||
					(isNextYearVignette && correspondingOfferSellToDateTime > newStartDateTime && updatedDates)
				) {
					onStartDateChange(correspondingTransitionVignette, updatedDates);
				} else {
					onStartDateChange(currentOffer, updatedDates);
				}
			} else {
				onStartDateChange(currentOffer, updatedDates);
			}
		}
	};

	const renderOneDayVignetteValidityMessage = () => {
		const isOneDayVignette = oneDayVignettes.some((code) => currentOffer?.serviceCatalogCode.endsWith(code));
		const isInTimeFrame = new Date().getHours() >= 20;
		const todaysDate = new Date().toLocaleDateString('de-DE', { dateStyle: 'medium' });
		const conditions = isOneDayVignette && isInTimeFrame;

		if (!conditions) return null;
		return (
			<HintMessage
				className="w-full mt-3 mx-3 mb-1.5"
				hints={getHintContent(
					'calendarHint',
					[
						{
							tip: 'Achtung',
							message: `Wenn Sie jetzt Ihre 1-Tages-Vignette für den ${todaysDate} buchen, ist diese nur noch bis 23:59 Uhr gültig.`,
						},
					],
					currentLabels?.hintMessages,
					undefined,
					todaysDate,
				)}
			/>
		);
	};

	const renderOneCartSloveniaRules = () => {
		let difference;
		if (dates) {
			const date1 = new Date().getTime();
			const date2 = new Date(dates.start).getTime();
			const diffTime = Math.abs(date2 - date1);
			difference = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		}
		const condition = hasAutVignette && hasSloVignette && difference >= 29;
		return (
			condition && (
				<HintMessage
					className="w-full mt-3 mx-3 mb-1.5"
					hints={getHintContent(
						'slo30DaysHint',
						[
							{
								tip: 'Hinweis:',
								message:
									'Die slowenische Vignette kann maximal 30 Tage im Voraus gebucht werden. Für die österreichische Vignette gibt es keine zeitliche Einschränkung - Sie können sie jederzeit bestellen.',
							},
						],
						currentLabels?.hintMessages,
					)}
				/>
			)
		);
	};

	const renderSloveniaHintMessage = () =>
		isSlovenianFlow &&
		inline && (
			<HintMessage
				className="w-full mt-2 mx-3 mb-6 md:mb-3"
				hints={getHintContent(
					'30Days',
					[
						{
							message:
								'Sie können das Startdatum Ihrer digitalen Vignette ab dem heutigen Datum maximal 30 Tage in der Zukunft wählen.',
							tip: 'Hinweis',
						},
					],
					currentLabels?.hintMessages,
				)}
			/>
		);

	const setExcludeDateIntervals = () =>
		excludeDateIntervals &&
		currentOffer && [
			{
				start: addDays(new Date(), currentOffer.maxDaysSellUsage - 1),
				end: vignetteForEndDate ? convertApiDate(vignetteForEndDate.sellTo) : new Date(nextYear, 0, 1),
			},
		];

	return (
		<>
			<div className="flex max-w-[744px] md:block">
				{hasError &&
					renderErrorMessage({
						message: currentLabels?.errorStartDate || 'Bitte wählen Sie ein Startdatum',
						className: 'w-full mt-3 mx-3 mb-1.5',
					})}
				{renderOneDayVignetteValidityMessage()}
				{renderOneCartSloveniaRules()}
				{renderSloveniaHintMessage()}
			</div>
			<div
				data-cy={dataCy}
				className={`calendar-container pointer-events-auto ${
					inline ? 'flex justify-center flex-col items-center' : ''
				}`}>
				<DatePicker
					selectsRange
					selectsDisabledDaysInRange
					isClearable
					inline={inline}
					placeholderText="Startdatum"
					dateFormat="dd.MM.yyyy"
					locale={lang ?? 'de'}
					startDate={dates?.start ?? currSelectedProduct?.dates?.start}
					endDate={dates?.start ?? currSelectedProduct?.dates?.start}
					monthsShown={viewport === 'desktop' ? 2 : 1}
					excludeDateIntervals={setExcludeDateIntervals()}
					minDate={vignetteStartDate}
					maxDate={vignetteForEndDate ? convertApiDate(vignetteForEndDate.sellTo) : new Date(nextYear, 0, 1)}
					customInput={customInput ? React.createElement(customInput) : undefined}
					renderDayContents={renderDayContents}
					onChange={(update) => {
						onChange(update);
					}}
				/>
			</div>
			{isTransitionPeriod && hasAutVignette && (
				<div className="flex justify-end mb-3">
					{currAutOffer && dates && (
						<>
							<Typography color="lightText">
								{currAutOffer.serviceCatalogName} {currAutOffer.servcatSeason}:
							</Typography>
							<Typography color="title" variant="title" className="mr-4 ml-1">
								{getPriceAmountByCatalogId(currAutOffer.priceComponents)}€
							</Typography>
						</>
					)}
				</div>
			)}
		</>
	);
};
